import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Image from '@atlaskit/image';
import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import Portal from '@atlaskit/portal';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	UNSAFE_UpdateOnChange as PopoverUpdateOnChange,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
	type Placement,
} from '@atlaskit/spotlight';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	CONTENT_TREE_BULK_ACTIONS,
	ONBOARDING_EXPERIENCES,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

import bulkActionsChangeboardingSpotlightImage from './assets/bulkActionsChangeboardingSpotlightImage.svg';

type BulkActionsChangeboardingSpotlightProps = {
	children: React.ReactNode;
	spaceKey: string;
	offset?: [number, number];
	placement?: Placement;
};

const i18n = defineMessages({
	changeboardingSpotlightHeading: {
		id: 'page-tree.bulk-actions.changeboarding-spotlight.heading',
		defaultMessage: 'Bulk actions in trees',
		description: 'Heading for the bulk actions changeboarding spotlight.',
	},
	changeboardingSpotlightDescription: {
		id: 'page-tree.bulk-actions.changeboarding-spotlight.description',
		defaultMessage:
			'Quickly organize your space with Bulk Actions!<br></br><br></br>Select multiple pages directly from the navigation tree and take action—delete or archive—in just a few clicks.',
		description: 'Description for the bulk actions changeboarding spotlight.',
	},
	changeboardingSpotlightDismissButton: {
		id: 'page-tree.bulk-actions.changeboarding-spotlight.dismissButton',
		defaultMessage: 'OK',
		description: 'Button text to dismiss the bulk actions changeboarding spotlight.',
	},
});

/** Stable hook name for React Compiler; do not destructure `useChoreographer` from {@link useChoreographerAssets}. */
function useChoreographerFromAssets(
	messageId: string,
	callbacks: { start?: () => void; stop?: () => void },
	messageTtl?: number,
) {
	const assets = useChoreographerAssets();
	// `useChoreographer` is only on the IPM factory object; compiler flags property access. Bare disable avoids
	// `react-compiler/react-compiler` when that rule is not registered (e.g. some ESLint workspace roots).
	// eslint-disable-next-line
	return assets.useChoreographer(messageId, callbacks, messageTtl);
}

export const BulkActionsChangeboardingSpotlight = ({
	children,
	spaceKey,
	offset = [0, 8],
	placement = 'right-end',
}: BulkActionsChangeboardingSpotlightProps) => {
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const expandSideNav = useExpandSideNav();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const {
		data: onboardingStateData,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(
		[
			CONTENT_TREE_BULK_ACTIONS.BULK_ACTIONS_CHANGEBOARDING_SPOTLIGHT_DISMISSED,
			ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
		],
		!expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false),
	);

	const { isFoundingAdmin } = deserializeState(onboardingStateData);
	const { isBulkActionsChangeboardingSpotlightDismissed } = deserializeState(onboardingStateData);

	const handleSpotlightStart = useCallback(() => {
		const timestamp = new Date().toISOString();

		expandSideNav();
		setOnboardingState({
			key: CONTENT_TREE_BULK_ACTIONS.BULK_ACTIONS_CHANGEBOARDING_SPOTLIGHT_DISMISSED,
			value: timestamp,
		});
		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'BulkActionsChangeboardingSpotlight',
				attributes: { spaceKey },
			},
		}).fire();
	}, [expandSideNav, setOnboardingState, createAnalyticsEvent, spaceKey]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const choreographer = useChoreographerFromAssets('BulkActionsChangeboardingSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleDismiss = useCallback(() => {
		void choreographer.stopMessage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'BulkActionsChangeboardingSpotlight',
				source: 'contentTreeBulkActions',
				attributes: { spaceKey },
			},
		}).fire();
	}, [choreographer, createAnalyticsEvent, spaceKey]);

	useEffect(() => {
		const loadingCompleteWithoutError = !isOnboardingStateLoading && !isOnboardingStateError;

		if (
			!isFoundingAdmin &&
			loadingCompleteWithoutError &&
			!!onboardingStateData &&
			!isBulkActionsChangeboardingSpotlightDismissed &&
			expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false)
		) {
			void choreographer.startMessage();
		}
	}, [
		isFoundingAdmin,
		choreographer,
		onboardingStateData,
		isBulkActionsChangeboardingSpotlightDismissed,
		isOnboardingStateLoading,
		isOnboardingStateError,
	]);

	if (!expVal('confluence_bulk_actions_in_content_tree', 'isEnabled', false) || isFoundingAdmin) {
		return children;
	}

	return (
		<PopoverProvider>
			<PopoverUpdateOnChange />
			<PopoverTarget>{children}</PopoverTarget>
			<Portal zIndex={600}>
				<PopoverContent
					dismiss={handleDismiss}
					isVisible={isSpotlightActive}
					placement={placement}
					offset={offset}
				>
					{(() => {
						const spotlightCard = (
							<SpotlightCard testId="bulk-actions-changeboarding-spotlight">
								<SpotlightHeader>
									<SpotlightHeadline>
										{formatMessage(i18n.changeboardingSpotlightHeading)}
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl onClick={handleDismiss} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightMedia>
									<Image src={bulkActionsChangeboardingSpotlightImage} alt="" width="100%" />
								</SpotlightMedia>
								<SpotlightBody>
									<Text>
										{formatMessage(i18n.changeboardingSpotlightDescription, {
											br: () => <br />,
										})}
									</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction onClick={handleDismiss}>
											{formatMessage(i18n.changeboardingSpotlightDismissButton)}
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						);
						return fg('platform-dst-motion-uplift-spotlight') ? (
							spotlightCard
						) : (
							<FadeIn>{(props) => <div {...props}>{spotlightCard}</div>}</FadeIn>
						);
					})()}
				</PopoverContent>
			</Portal>
		</PopoverProvider>
	);
};
